import { defineStore } from 'pinia';

import { createPendingEntries } from '@/modules/companyNeed/helpers/pendingEntries';

const createHiddenIds = () => {
	const hidden = createPendingEntries<true>();

	const mark = (id: string) => {
		if (!id || hidden.isFresh(id)) return;
		hidden.set(id, true);
	};

	const prune = () => hidden.reconcile(() => false);

	return {
		ids: hidden.entries,
		has: hidden.isFresh,
		mark,
		unmark: hidden.remove,
		prune,
	};
};

/**
 * Tracks company needs changed optimistically so lists can hide them even
 * while the CQRS query-side projection has not caught up with the command:
 * - deleted or archived needs are hidden from the open list,
 * - reopened needs are hidden from the archived list.
 * Ids stay hidden for PENDING_TTL_MS, whatever page the list is currently showing.
 */
export const useCompanyNeedVisibilityStore = defineStore(
	'companyNeedVisibility',
	() => {
		const deleted = createHiddenIds();
		const reopened = createHiddenIds();

		const markDeleted = (id: string) => {
			reopened.unmark(id);
			deleted.mark(id);
		};

		const markReopened = (id: string) => {
			deleted.unmark(id);
			reopened.mark(id);
		};

		const prune = () => {
			deleted.prune();
			reopened.prune();
		};

		return {
			deletedIds: deleted.ids,
			isDeleted: deleted.has,
			markDeleted,
			unmarkDeleted: deleted.unmark,
			reopenedIds: reopened.ids,
			isReopened: reopened.has,
			markReopened,
			unmarkReopened: reopened.unmark,
			prune,
		};
	}
);
